import type { Temporal } from '@insio/dates';
import { ApprovalStatus } from './approval-status';

/**
 * Requisition Status
 *
 * The legacy lifecycle status of a purchase requisition (žádanka), derived from its
 * `ApprovalStatus`; never stored. History and the AI chat speak it. Ordered and completion are flags.
 */
export const RequisitionStatus = {
  /** Being prepared (Rozpracovaná) — approval UNPROCESSED */
  DRAFT: 'DRAFT',
  /** Under approval (Ke schválení) — approval PENDING or FOR_REVIEW */
  FOR_APPROVAL: 'FOR_APPROVAL',
  /** Approved — may be turned into orders (Schválená) */
  APPROVED: 'APPROVED',
  /** Refused (Neschválená) */
  REJECTED: 'REJECTED'
} as const;

export type RequisitionStatus = (typeof RequisitionStatus)[keyof typeof RequisitionStatus];

const REQUISITION_STATUS_OF: Record<ApprovalStatus, RequisitionStatus> = {
  UNPROCESSED: RequisitionStatus.DRAFT,
  PENDING: RequisitionStatus.FOR_APPROVAL,
  FOR_REVIEW: RequisitionStatus.FOR_APPROVAL,
  APPROVED: RequisitionStatus.APPROVED,
  REJECTED: RequisitionStatus.REJECTED
};

/** The legacy status an approval status is written and shown as. */
export const requisitionStatusOf = (approvalStatus: ApprovalStatus): RequisitionStatus =>
  REQUISITION_STATUS_OF[approvalStatus];

/** The approval statuses a legacy status stands for — what a filter on it selects. */
export const requisitionApprovalStatusesOf = (status: RequisitionStatus): ApprovalStatus[] =>
  Object.values(ApprovalStatus).filter(
    approvalStatus => requisitionStatusOf(approvalStatus) === status
  );

/** The approval status a move to a legacy target status lands in; under approval is PENDING. */
export const requisitionApprovalTargetOf = (status: RequisitionStatus): ApprovalStatus => {
  switch (status) {
    case RequisitionStatus.DRAFT:
      return ApprovalStatus.UNPROCESSED;
    case RequisitionStatus.FOR_APPROVAL:
      return ApprovalStatus.PENDING;
    case RequisitionStatus.APPROVED:
      return ApprovalStatus.APPROVED;
    case RequisitionStatus.REJECTED:
      return ApprovalStatus.REJECTED;
  }
};

/**
 * Allowed approval moves. A move absent from this map is rejected — the state machine is
 * enforced, never assigned blindly.
 */
export const REQUISITION_STATUS_TRANSITIONS: Record<ApprovalStatus, readonly ApprovalStatus[]> = {
  UNPROCESSED: [ApprovalStatus.PENDING],
  // Back to UNPROCESSED withdraws the open approval round.
  PENDING: [
    ApprovalStatus.FOR_REVIEW,
    ApprovalStatus.APPROVED,
    ApprovalStatus.REJECTED,
    ApprovalStatus.UNPROCESSED
  ],
  FOR_REVIEW: [ApprovalStatus.APPROVED, ApprovalStatus.REJECTED, ApprovalStatus.UNPROCESSED],
  // Ordered is the `orderedAt` flag coverage sets, never a status a person moves to.
  APPROVED: [],
  REJECTED: [ApprovalStatus.UNPROCESSED]
};

/** Whether the approval move is one the lifecycle allows. */
export const isRequisitionStatusTransitionAllowed = (
  from: ApprovalStatus,
  to: ApprovalStatus
): boolean => REQUISITION_STATUS_TRANSITIONS[from].includes(to);

/** A requisition as the ordered flag reads it. */
export interface OrderableRequisition {
  readonly orderedAt?: Temporal.Instant | null;
}

/** Whether every line of the requisition is covered by a purchase order. */
export const isRequisitionOrdered = (requisition: OrderableRequisition): boolean =>
  requisition.orderedAt != null;

/** Whether a requisition may still be edited: approval not started; a rejected one goes back first. */
export const isRequisitionEditable = (approvalStatus: ApprovalStatus): boolean =>
  approvalStatus === ApprovalStatus.UNPROCESSED;

/** Whether a requisition with this approval status may be taken back for editing. */
export const canReturnRequisitionToDraft = (approvalStatus: ApprovalStatus): boolean =>
  isRequisitionStatusTransitionAllowed(approvalStatus, ApprovalStatus.UNPROCESSED);

/** Whether the requisition's approval is running or waiting for a user. */
export const isRequisitionUnderApproval = (approvalStatus: ApprovalStatus): boolean =>
  approvalStatus === ApprovalStatus.PENDING || approvalStatus === ApprovalStatus.FOR_REVIEW;

/** Requisitions held for a decision — the automation's or a person's. */
export const REQUISITION_AWAITING_DECISION_STATUSES: readonly ApprovalStatus[] = [
  ApprovalStatus.PENDING,
  ApprovalStatus.FOR_REVIEW
];

/** Submitted and not yet finished. `REJECTED` is excluded — it is returned, not in flight. */
export const REQUISITION_IN_FLIGHT_STATUSES: readonly ApprovalStatus[] = [
  ApprovalStatus.PENDING,
  ApprovalStatus.FOR_REVIEW,
  ApprovalStatus.APPROVED
];

/**
 * Approval statuses a requisition may be cancelled from (`cancelledAt`, not a status) — before a
 * decision or after a rejection; an approved one may have orders.
 */
export const REQUISITION_CANCELLABLE_STATUSES: readonly ApprovalStatus[] = [
  ApprovalStatus.UNPROCESSED,
  ApprovalStatus.REJECTED
];

/** Whether a requisition with this approval status may be cancelled. */
export const isRequisitionCancellable = (approvalStatus: ApprovalStatus): boolean =>
  REQUISITION_CANCELLABLE_STATUSES.includes(approvalStatus);
